export enum NioshCouplingQuality {
  GOOD = "GOOD",
  FAIR = "FAIR",
  POOR = "POOR",
}

export type DiscreteTable = Record<number, number> & { [key: string]: number };

export const HORIZONTAL_MULTIPLIER_TABLE: DiscreteTable = {
  0: 1.0,
  10: 1.0,
  11: 0.91,
  12: 0.83,
  13: 0.77,
  14: 0.71,
  15: 0.67,
  16: 0.63,
  17: 0.59,
  18: 0.56,
  19: 0.53,
  20: 0.5,
  21: 0.48,
  22: 0.46,
  23: 0.44,
  24: 0.42,
  25: 0.4,
  ">25": 0.0,
};

/**
 * Returns HM – **Horizontal Multiplier**.
 * If `H` exceeds 25 in, 0 is returned (out‑of‑range per RNLE rules).
 */
export function getHorizontalMultiplier(horizontalDistanceIn: number): number {
  if (horizontalDistanceIn <= 10) return 1.0;
  if (horizontalDistanceIn > 25) return 0.0;

  return HORIZONTAL_MULTIPLIER_TABLE[Math.round(horizontalDistanceIn)] ?? 0.0;
}

export const VERTICAL_MULTIPLIER_TABLE: DiscreteTable = {
  0: 0.78,
  5: 0.81,
  10: 0.85,
  15: 0.89,
  20: 0.93,
  25: 0.96,
  30: 1.0,
  35: 0.96,
  40: 0.93,
  45: 0.89,
  50: 0.85,
  55: 0.81,
  60: 0.78,
  65: 0.74,
  70: 0.7,
  ">70": 0.0,
};

export function getVerticalMultiplier(verticalHeightIn: number): number {
  if (verticalHeightIn < 0) return 0.0; // impossible height
  if (verticalHeightIn > 70) return 0.0;
  // Snap to nearest 5‑inch increment present in the table
  const rounded = Math.round(verticalHeightIn / 5) * 5;
  const key = rounded as unknown as keyof typeof VERTICAL_MULTIPLIER_TABLE;
  return VERTICAL_MULTIPLIER_TABLE[key] ?? 0.0;
}

export const DISTANCE_MULTIPLIER_TABLE: DiscreteTable = {
  0: 1.0,
  10: 1.0,
  15: 0.94,
  20: 0.91,
  25: 0.89,
  30: 0.88,
  35: 0.87,
  40: 0.87,
  45: 0.86,
  50: 0.86,
  55: 0.85,
  60: 0.85,
  65: 0.85,
  70: 0.85,
  ">70": 0.0,
};

export function getDistanceMultiplier(
  verticalTravelDistanceIn: number,
): number {
  if (verticalTravelDistanceIn > 70) return 0.0;
  if (verticalTravelDistanceIn <= 10) return 1.0;
  return DISTANCE_MULTIPLIER_TABLE[
    Math.round(verticalTravelDistanceIn / 5) * 5
  ];
}

export const ASYMMETRIC_MULTIPLIER_TABLE: DiscreteTable = {
  0: 1.0,
  15: 0.95,
  30: 0.9,
  45: 0.86,
  60: 0.81,
  75: 0.76,
  90: 0.71,
  105: 0.66,
  120: 0.62,
  135: 0.57,
  ">135": 0.0,
};

export function getAsymmetricMultiplier(asymmetryAngleDeg: number): number {
  if (asymmetryAngleDeg > 135) return 0.0;
  // Round to nearest 15° increment
  const rounded = Math.round(asymmetryAngleDeg / 15) * 15;
  const key = rounded as unknown as keyof typeof ASYMMETRIC_MULTIPLIER_TABLE;
  return ASYMMETRIC_MULTIPLIER_TABLE[key] ?? 0.0;
}

/** Duration categories per RNLE. */
export type DurationCategory = "LE1H" | "GT1LE2H" | "GT2LE8H";

/** Two vertical‑height bands */
export type VerticalBand = "LT30" | "GE30";

interface FrequencyRow {
  freq: number | ">15"; // lifts per minute
  values: Record<DurationCategory, Record<VerticalBand, number>>;
}

// prettier‑ignore
export const FREQUENCY_MULTIPLIER_TABLE: FrequencyRow[] = [
  {
    freq: 0.2,
    values: {
      GT2LE8H: { LT30: 0.85, GE30: 0.85 },
      GT1LE2H: { LT30: 0.95, GE30: 0.95 },
      LE1H: { LT30: 1.0, GE30: 1.0 },
    },
  },
  {
    freq: 0.5,
    values: {
      GT2LE8H: { LT30: 0.81, GE30: 0.81 },
      GT1LE2H: { LT30: 0.92, GE30: 0.92 },
      LE1H: { LT30: 0.97, GE30: 0.97 },
    },
  },
  {
    freq: 1,
    values: {
      GT2LE8H: { LT30: 0.75, GE30: 0.75 },
      GT1LE2H: { LT30: 0.88, GE30: 0.88 },
      LE1H: { LT30: 0.94, GE30: 0.94 },
    },
  },
  {
    freq: 2,
    values: {
      GT2LE8H: { LT30: 0.65, GE30: 0.65 },
      GT1LE2H: { LT30: 0.84, GE30: 0.84 },
      LE1H: { LT30: 0.91, GE30: 0.91 },
    },
  },
  {
    freq: 3,
    values: {
      GT2LE8H: { LT30: 0.55, GE30: 0.55 },
      GT1LE2H: { LT30: 0.79, GE30: 0.79 },
      LE1H: { LT30: 0.88, GE30: 0.88 },
    },
  },
  {
    freq: 4,
    values: {
      GT2LE8H: { LT30: 0.45, GE30: 0.45 },
      GT1LE2H: { LT30: 0.72, GE30: 0.72 },
      LE1H: { LT30: 0.84, GE30: 0.84 },
    },
  },
  {
    freq: 5,
    values: {
      GT2LE8H: { LT30: 0.35, GE30: 0.35 },
      GT1LE2H: { LT30: 0.6, GE30: 0.6 },
      LE1H: { LT30: 0.8, GE30: 0.8 },
    },
  },
  {
    freq: 6,
    values: {
      GT2LE8H: { LT30: 0.27, GE30: 0.27 },
      GT1LE2H: { LT30: 0.5, GE30: 0.5 },
      LE1H: { LT30: 0.75, GE30: 0.75 },
    },
  },
  {
    freq: 7,
    values: {
      GT2LE8H: { LT30: 0.22, GE30: 0.22 },
      GT1LE2H: { LT30: 0.42, GE30: 0.42 },
      LE1H: { LT30: 0.7, GE30: 0.7 },
    },
  },
  {
    freq: 8,
    values: {
      GT2LE8H: { LT30: 0.18, GE30: 0.18 },
      GT1LE2H: { LT30: 0.35, GE30: 0.35 },
      LE1H: { LT30: 0.6, GE30: 0.6 },
    },
  },
  {
    freq: 9,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.15 },
      GT1LE2H: { LT30: 0.3, GE30: 0.3 },
      LE1H: { LT30: 0.52, GE30: 0.52 },
    },
  },
  {
    freq: 10,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.13 },
      GT1LE2H: { LT30: 0.26, GE30: 0.26 },
      LE1H: { LT30: 0.45, GE30: 0.45 },
    },
  },
  {
    freq: 11,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.23 },
      LE1H: { LT30: 0.41, GE30: 0.41 },
    },
  },
  {
    freq: 12,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.21 },
      LE1H: { LT30: 0.37, GE30: 0.37 },
    },
  },
  {
    freq: 13,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.0 },
      LE1H: { LT30: 0.34, GE30: 0.34 },
    },
  },
  {
    freq: 14,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.0 },
      LE1H: { LT30: 0.0, GE30: 0.31 },
    },
  },
  {
    freq: 15,
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.0 },
      LE1H: { LT30: 0.0, GE30: 0.28 },
    },
  },
  {
    freq: ">15",
    values: {
      GT2LE8H: { LT30: 0.0, GE30: 0.0 },
      GT1LE2H: { LT30: 0.0, GE30: 0.0 },
      LE1H: { LT30: 0.0, GE30: 0.0 },
    },
  },
];

/**
 * Returns FM – **Frequency Multiplier**.
 *
 * @param liftsPerMin   Lift rate (lifts · min⁻¹)
 * @param durationHrs   Continuous task duration in **hours**
 * @param verticalIn    Hand height (in) *at the origin/destination* used to
 *                      choose the <30 in / ≥30 in vertical band.
 */
export function getFrequencyMultiplier(
  liftsPerMin: number,
  durationHrs: number,
  verticalIn: number,
): number {
  const durationCat: DurationCategory =
    durationHrs <= 1 ? "LE1H" : durationHrs <= 2 ? "GT1LE2H" : "GT2LE8H";
  const vBand: VerticalBand = verticalIn < 30 ? "LT30" : "GE30";

  // Locate first table row whose threshold ≥ input frequency.
  for (const row of FREQUENCY_MULTIPLIER_TABLE) {
    if (typeof row.freq === "number" && liftsPerMin <= row.freq) {
      return row.values[durationCat][vBand];
    }
  }
  // If >15 lifts/min, the last row (">15") is all zeros.
  return 0.0;
}

export const COUPLING_FACTOR_TABLE: Record<
  NioshCouplingQuality,
  Record<VerticalBand, number>
> = {
  [NioshCouplingQuality.GOOD]: { LT30: 1.0, GE30: 1.0 },
  [NioshCouplingQuality.FAIR]: { LT30: 0.95, GE30: 1.0 },
  [NioshCouplingQuality.POOR]: { LT30: 0.9, GE30: 0.9 },
};

export function getCouplingMultiplier(
  quality: NioshCouplingQuality,
  verticalIn: number,
): number {
  const vBand: VerticalBand = verticalIn < 30 ? "LT30" : "GE30";
  return COUPLING_FACTOR_TABLE[quality][vBand];
}
